import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Fade,
  Slide,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { RandomizedText } from '../App';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FeedPage = ({
  onNavigate,
  feedItems: initialFeedItems,
  loading,
  error,
  newItemId,
  animateItemId,
  loadMoreItems,
  hasMore,
}) => {
  const [autoplay, setAutoplay] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const audioRefs = useRef({});
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  // Set initial load complete after a delay
  useEffect(() => {
    if (initialFeedItems && initialFeedItems.length > 0 && !initialLoadComplete) {
      // Mark initial load as complete after a small delay
      // to prevent animations on first render
      setTimeout(() => {
        setInitialLoadComplete(true);
      }, 500);
    }
  }, [initialFeedItems, initialLoadComplete]);

  // Setup IntersectionObserver for infinite scroll
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreItems();
          }
        },
        { threshold: 0.5 }
      );

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, loadMoreItems]
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleAutoplayToggle = () => {
    setAutoplay(!autoplay);
  };

  const handleAudioEnded = (index) => {
    if (autoplay && index < initialFeedItems.length - 1) {
      // Play the next audio
      const nextIndex = index + 1;
      setCurrentlyPlaying(nextIndex);
      const nextAudio = audioRefs.current[initialFeedItems[nextIndex].filename];
      if (nextAudio) {
        nextAudio.play();
      }
    } else {
      setCurrentlyPlaying(null);
    }
  };

  const handleAudioPlay = (index) => {
    // Pause any currently playing audio
    if (currentlyPlaying !== null && currentlyPlaying !== index) {
      const currentAudio = audioRefs.current[initialFeedItems[currentlyPlaying].filename];
      if (currentAudio) {
        currentAudio.pause();
      }
    }
    setCurrentlyPlaying(index);

    // Check if this is the last item in the feed and preemptively load more if available
    if (index === initialFeedItems.length - 1 && hasMore && !loading) {
      loadMoreItems();
    }
  };

  const renderAudioCard = (item, index) => {
    // Determine if this is the new item (for styling and badge)
    const isNewItem = item.filename === newItemId;

    // Determine if this item should animate (only if it was just added via WebSocket)
    const shouldAnimate = item.filename === animateItemId;

    // For the last element, add ref for infinite scroll
    const isLastElement = index === initialFeedItems.length - 1;

    return (
      <Slide
        direction="down"
        in={true}
        mountOnEnter
        unmountOnExit
        timeout={shouldAnimate ? 500 : 0} // Only animate if it was just added via WebSocket
        key={item.filename}
      >
        <Grid item xs={12} ref={isLastElement ? lastElementRef : null}>
          <Fade
            in={true}
            timeout={shouldAnimate ? { enter: 800 } : 0} // Only animate if it was just added via WebSocket
            style={{
              transitionDelay: shouldAnimate ? '300ms' : '0ms',
            }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: isNewItem ? 'rgba(187, 134, 252, 0.08)' : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border:
                  currentlyPlaying === index
                    ? '1px solid #bb86fc'
                    : isNewItem
                      ? '1px solid rgba(187, 134, 252, 0.3)'
                      : 'none',
                transition: 'all 0.5s ease',
                position: 'relative',
                overflow: 'visible',
                transform: isNewItem ? 'scale(1.01)' : 'scale(1)',
                boxShadow: isNewItem ? '0px 5px 15px rgba(187, 134, 252, 0.15)' : 'none',
                animation: shouldAnimate ? 'newItemPulse 1s ease-out forwards' : 'none',
                '@keyframes newItemPulse': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.98)',
                  },
                  '70%': {
                    opacity: 1,
                    transform: 'scale(1.02)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                },
              }}
            >
              {isNewItem && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -12,
                    right: -12,
                    background: '#bb86fc',
                    color: '#121212',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                    animation: 'badgePulse 3.5s infinite',
                    zIndex: 10,
                    '@keyframes badgePulse': {
                      '0%': { transform: 'scale(1)', background: 'hsl(266.9,95.2%,75.7%)' },
                      '50%': { transform: 'scale(1.05)', background: 'hsl(266.9,95.2%,70%)' },
                      '100%': { transform: 'scale(1)', background: 'hsl(266.9,95.2%,75.7%)' },
                    },
                  }}
                >
                  NEW
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body1"
                  component="h4"
                  gutterBottom
                  sx={{
                    maxWidth: '80%',
                    mx: 'auto',
                    textAlign: 'center',
                  }}
                >
                  {item.filename}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Shared on {formatDate(item.shared_at)}
                </Typography>

                <Box>
                  <audio
                    controls
                    style={{ width: '100%' }}
                    ref={(el) => (audioRefs.current[item.filename] = el)}
                    onPlay={() => handleAudioPlay(index)}
                    onEnded={() => handleAudioEnded(index)}
                  >
                    <source src={`${API_URL}/download/${item.filename}`} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Slide>
    );
  };

  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <RandomizedText
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          maxWidth: '80%',
          mx: 'auto',
          mb: 6,
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        POV: You're crying in the bathroom listening to...
      </RandomizedText>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => onNavigate('/')}
          color="secondary"
          sx={{
            mr: 'auto',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(138, 152, 157, 0.1)',
            },
          }}
        >
          Back
        </Button>

        <FormControlLabel
          control={<Switch checked={autoplay} onChange={handleAutoplayToggle} color="primary" />}
          label="Autoplay next track"
        />
      </Box>

      {initialFeedItems.length === 0 && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : error && initialFeedItems.length === 0 ? (
        <Typography color="error" align="center" sx={{ my: 8 }}>
          {error}
        </Typography>
      ) : initialFeedItems.length === 0 ? (
        <Typography align="center" sx={{ my: 8 }}>
          No shared audio yet. Be the first to share!
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {initialFeedItems.map((item, index) => renderAudioCard(item, index))}
          </Grid>

          {/* Loading indicator at the bottom */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }} ref={loadingRef}>
              <CircularProgress size={30} />
            </Box>
          )}

          {/* End of feed message */}
          {!loading && !hasMore && initialFeedItems.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 4, mb: 8 }}>
                the end :)
              </Typography>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => onNavigate('/')}
                color="secondary"
                sx={{
                  mr: 'auto',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(138, 152, 157, 0.1)',
                  },
                }}
              >
                Back
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default FeedPage;
